import React, { useState, useEffect, useMemo } from 'react'
import { Modal, Button } from 'react-bootstrap'
import { IoIosArrowDown } from 'react-icons/io'
import { i18nTranslate, IS_BROWSER, isINMarket } from 'src/utils'
import { APPConfig } from 'config/appConfig'
import { multipleSubscriptionContainer } from 'src/models'
import { toastState, overlayState } from 'src/views/components'

const AuthorizationLimitInfoModal = ({
  show = false,
  currentLimit = 10000,
  subscriptionId = '',
  onClose,
  onContinue,
  isLimitExceeded = false,
}) => {
  const emandateLimitOptionsForApproval =
    APPConfig?.getAppConfig()?.emandateLimitOptionsForApproval ||
    '[10000,20000,30000,40000,50000,60000,70000,80000]'

  const allLimitOptions = useMemo(() => {
    try {
      return JSON.parse(emandateLimitOptionsForApproval) || []
    } catch {
      return []
    }
  }, [emandateLimitOptionsForApproval])

  const availableLimitOptions = useMemo(
    () => allLimitOptions.filter(option => option > currentLimit),
    [allLimitOptions, currentLimit]
  )

  const isAtMaxLimit = currentLimit >= 80000

  const defaultSelectedLimit =
    isAtMaxLimit || availableLimitOptions.length === 0
      ? 80000
      : availableLimitOptions[0]

  const [selectedLimit, setSelectedLimit] = useState(defaultSelectedLimit)

  useEffect(() => {
    const newDefault =
      isAtMaxLimit || availableLimitOptions.length === 0
        ? 80000
        : availableLimitOptions[0]
    setSelectedLimit(newDefault)
  }, [availableLimitOptions, show, isAtMaxLimit])

  const handleClose = () => {
    onClose?.()
  }

  const handleContinue = async () => {
    if (!subscriptionId) return

    const landingUrl = IS_BROWSER ? window.location.href : ''

    if (landingUrl) {
      sessionStorage.setItem('paymentAuthorizationLandingUrl', landingUrl)
    }

    overlayState.showLoader()

    const payload = {
      mandateLimit: {
        maxGrandTotalLimit: selectedLimit,
      },
      landingUrl,
    }

    const response =
      await multipleSubscriptionContainer.createSubscriptionEMandate(
        subscriptionId,
        payload
      )

    overlayState.hideLoader()

    if (multipleSubscriptionContainer.isSuccessResponse(response)) {
      const redirectUrl = response?.redirectUrl

      if (redirectUrl && IS_BROWSER) {
        const gpsOrderId = redirectUrl.split('/').pop()

        sessionStorage.setItem(
          'paymentAuthorizationSubscriptionId',
          subscriptionId
        )
        if (gpsOrderId) {
          sessionStorage.setItem('paymentAuthorizationOrderId', gpsOrderId)
        }

        window.location.href = redirectUrl
        return
      }
    }

    onContinue?.(selectedLimit)
  }

  const titleText = isLimitExceeded
    ? i18nTranslate(
        'subscription.changeExceedsLimit',
        'This change exceeds your approved limit.'
      )
    : i18nTranslate(
        'subscription.increaseApprovedLimit',
        'Increase Your Approved Limit'
      )

  const descriptionText = isLimitExceeded
    ? i18nTranslate(
        'subscription.approveHigherLimitFirst',
        "To continue, you'll need to approve a higher limit first. After approval, return here to finish making your subscription changes."
      )
    : i18nTranslate(
        'subscription.updateApprovedLimitDescription',
        'Update your approved limit to support future subscription changes. Select a new limit and approve it to continue.'
      )

  const maxLimitNote = i18nTranslate(
    'subscription.maxLimitNote',
    'Note that an eMandate limit of more than ₹80,000 will require a new subscription.'
  )

  const currentLimitLabel = i18nTranslate(
    'subscription.currentLimit',
    'Current limit'
  )
  const newLimitLabel = i18nTranslate('subscription.newLimit', 'New limit')
  const approveNewLimitLabel = i18nTranslate(
    'subscription.approveNewLimit',
    'Approve new limit'
  )
  const cancelLabel = isLimitExceeded
    ? i18nTranslate('subscription.cancelChange', 'Cancel change')
    : i18nTranslate('subscription.cancel', 'Cancel')

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="authorization-limit-modal-wrapper"
      data-testid="qa-authorization-limit-modal"
      centered>
      <Modal.Body data-testid="qa-subscription-amount-limit-wrapper">
        <div
          className="authorization-limit-modal-header"
          data-testid="qa-subscription-amount-limit-header">
          <span className="authorization-limit-modal-icon">⚠</span>
          <h5 className="authorization-limit-modal-title">{titleText}</h5>
        </div>
        <div className="authorization-limit-modal-body">
          <p data-testid="qa-subscription-amount-limit-body">
            {descriptionText}
          </p>
          {isAtMaxLimit && (
            <p data-testid="qa-subscription-max-limit-note">{maxLimitNote}</p>
          )}
          <div className="authorization-limit-fields">
            <div className="authorization-limit-field">
              <span className="limit-label">{currentLimitLabel}</span>
              <span className="limit-value" data-testid="qa-current-limit">
                ₹{currentLimit?.toLocaleString('en-IN')}
              </span>
            </div>
            <div className="authorization-limit-field-select">
              <span className="limit-label">{newLimitLabel}</span>
              <div className="limit-dropdown-wrapper">
                <span className="rupee-symbol">₹</span>
                <select
                  className="limit-select"
                  value={selectedLimit}
                  onChange={e => setSelectedLimit(Number(e.target.value))}
                  disabled={isAtMaxLimit}
                  data-testid="qa-new-limit-dropdown">
                  {isAtMaxLimit ? (
                    <option value={80000}>80,000</option>
                  ) : (
                    availableLimitOptions.map(option => (
                      <option key={option} value={option}>
                        {option?.toLocaleString('en-IN')}
                      </option>
                    ))
                  )}
                </select>
                <span className="dropdown-arrow">
                  <IoIosArrowDown />
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer data-testid="qa-subscription-amount-limit-footer">
        <Button
          variant="primary"
          onClick={handleContinue}
          disabled={isAtMaxLimit}
          className="subscription-amount-limit-continue"
          data-testid="subscription-amount-limit-continue-btn">
          {approveNewLimitLabel}
        </Button>
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          className="subscription-amount-limit-cancel"
          data-testid="subscription-amount-limit-cancel-btn">
          {cancelLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export { AuthorizationLimitInfoModal }
export default AuthorizationLimitInfoModal
